import { Link } from "react-router-dom";
import { ArrowRight, Check, Building2, GraduationCap } from "lucide-react";
import { coursesByCategory, CATEGORIES, COURSES } from "@/lib/data";
import SEO, { courseSchema } from "@/components/SEO";

export default function Corsi() {
    return (
        <>
            <SEO
                title="Corsi BLS-AED e Samaritani · Tutti i corsi"
                description="Tutti i corsi 7MED: BLS-AED SRC, BLS-AED + MISV e Workshop per aziende; Corso Samaritani per la patente di guida (CHF 140). Certificati SRC, EduQua, USTRA."
                path="/corsi"
                keywords="corsi BLS-AED Ticino, corsi primo soccorso, corso samaritani, workshop aziendali, certificato SRC"
                structuredData={[
                    {
                        "@context": "https://schema.org",
                        "@type": "ItemList",
                        itemListElement: COURSES.map((c, i) => ({
                            "@type": "ListItem",
                            position: i + 1,
                            url: `https://7med.ch${c.href}`,
                            name: c.fullTitle,
                        })),
                    },
                    ...COURSES.map((c) => courseSchema(c)),
                ]}
                breadcrumbs={[{ name: "Home", path: "/" }, { name: "Corsi", path: "/corsi" }]}
            />
            <section className="bg-ink text-white">
                <div className="container-7m py-16 sm:py-20 lg:py-28">
                    <div className="label-eyebrow !text-brand">I nostri corsi</div>
                    <h1 className="mt-4 font-heading font-black text-4xl sm:text-6xl lg:text-7xl tracking-tighter leading-[1.05] max-w-4xl">
                        Due percorsi.<br /><span className="text-brand">Cinque corsi.</span>
                    </h1>
                    <p className="mt-6 sm:mt-8 text-base sm:text-lg lg:text-xl text-white/85 max-w-2xl leading-relaxed">
                        Formazione obbligatoria per le aziende e per chi ottiene una patente di guida. Scegli la categoria che fa per te.
                    </p>
                </div>
            </section>

            {/* Per le aziende */}
            <CategoryBlock
                category="aziende"
                icon={Building2}
                bgClass="bg-white"
                accent="brand"
            />
            {/* Per la patente */}
            <CategoryBlock
                category="patente"
                icon={GraduationCap}
                bgClass="bg-[#FAFAFA]"
                accent="ink"
            />

            <section className="py-16 sm:py-20 lg:py-28 bg-ink text-white border-t border-white/10">
                <div className="container-7m text-center max-w-2xl mx-auto">
                    <div className="label-eyebrow !text-brand">Non sai quale scegliere?</div>
                    <h2 className="mt-4 font-heading font-black text-4xl lg:text-5xl tracking-tighter leading-tight">Ti guidiamo noi.</h2>
                    <p className="mt-6 text-readable-lg !text-white/80 mx-auto">
                        Chiamaci o scrivici: valutiamo insieme la formazione più adatta in base al tuo ruolo, alla normativa applicabile e alle esigenze della tua azienda o autoscuola.
                    </p>
                    <Link to="/contatti" className="btn-primary mt-10">Contattaci <ArrowRight className="w-4 h-4" /></Link>
                </div>
            </section>
        </>
    );
}

function CategoryBlock({ category, icon: Icon, bgClass, accent }) {
    const items = coursesByCategory(category);
    const cat = CATEGORIES[category];
    const accentBg = accent === "brand" ? "bg-brand" : "bg-ink";
    return (
        <section className={`py-16 sm:py-20 lg:py-28 ${bgClass}`}>
            <div className="container-7m">
                <div className="flex items-end justify-between mb-10 sm:mb-12 pb-5 border-b-2 border-ink">
                    <div className="flex items-center gap-4 sm:gap-5">
                        <div className={`w-12 h-12 sm:w-14 sm:h-14 ${accentBg} text-white flex items-center justify-center`}>
                            <Icon className="w-6 h-6 sm:w-7 sm:h-7" strokeWidth={2} />
                        </div>
                        <div>
                            <div className="text-[11px] sm:text-[12px] font-mono uppercase tracking-[0.18em] text-brand">Categoria</div>
                            <h2 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter leading-tight">{cat.label}</h2>
                        </div>
                    </div>
                    <div className="hidden sm:block text-xs font-mono text-ink-muted uppercase tracking-wider">{items.length} corsi</div>
                </div>

                <div className="space-y-10 sm:space-y-12">
                    {items.map((c, i) => (
                        <article key={c.slug} className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 border-t border-ink/10 pt-8 sm:pt-10" data-testid={`corsi-overview-${c.slug}`}>
                            <div className="lg:col-span-2 flex lg:flex-col gap-5 lg:gap-3 items-center lg:items-start">
                                <div className="font-heading font-black text-5xl lg:text-6xl text-brand tracking-tighter leading-none">0{i + 1}</div>
                                <c.menuIcon className="w-9 h-9 text-ink" strokeWidth={1.5} />
                            </div>
                            <div className="lg:col-span-6">
                                <div className="label-eyebrow">{c.eyebrow}</div>
                                <h3 className="mt-3 font-heading font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter leading-[1.05]">{c.title}</h3>
                                <p className="mt-3 text-readable-lg max-w-2xl">{c.tagline}</p>
                                <ul className="mt-5 space-y-2">
                                    {c.bullets.slice(0, 4).map((b) => (
                                        <li key={b} className="flex gap-3 text-[15px] sm:text-base leading-relaxed">
                                            <Check className="w-4 h-4 text-brand flex-shrink-0 mt-1" strokeWidth={2.5} />{b}
                                        </li>
                                    ))}
                                </ul>
                                <Link to={c.href} className="btn-primary mt-7" data-testid={`corsi-overview-cta-${c.slug}`}>
                                    Dettagli completi <ArrowRight className="w-4 h-4" />
                                </Link>
                            </div>
                            <div className="lg:col-span-4 bg-white border border-ink/10 p-6 lg:p-7">
                                <div className="space-y-3 text-[15px]">
                                    <Spec label="Durata" value={c.duration} />
                                    <Spec label="Gruppo" value={c.groupSize} />
                                    <Spec label="Certificazione" value={c.certification} />
                                    <Spec label="Prezzo" value={c.price || c.priceNote} highlight={!!c.price} />
                                </div>
                            </div>
                        </article>
                    ))}
                </div>
            </div>
        </section>
    );
}

function Spec({ label, value, highlight }) {
    return (
        <div className="flex justify-between gap-4 border-b border-ink/10 pb-3">
            <span className="text-[10px] uppercase tracking-[0.18em] font-mono text-ink-muted">{label}</span>
            <span className={`font-bold text-right ${highlight ? "text-brand text-lg" : ""}`}>{value}</span>
        </div>
    );
}
